import { isPlatformServer } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { from, of, Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { UserService } from '~/auth/services/user/user.service';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { BookingService } from '~/checkout/services/booking/booking.service';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { VtmnPlayButtonSizeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-size.enum';
import {
  googleCalendarUrl,
  icsUrl,
  outlookUrl,
} from '~/core/helpers/calendar/calendars.helper';
import { TitleService } from '~/core/services/title/title.service';
import { TalkService } from '~/messaging/services/talk.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { ActivityService } from '~/core/services/activity/activity.service';
import { QrCodeBookingInterface } from '~/organizer/shared/services/qr-code/interfaces/qr-code-interface';

@Component({
  selector: 'app-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
})
export class ConfirmationComponent implements OnInit, OnDestroy {
  @ViewChild('dummyInbox') public readonly inbox?: ElementRef;

  private readonly mediaMethods = {
    google: googleCalendarUrl,
    outlook: outlookUrl,
    ical: icsUrl,
  };
  private readonly subscriptions = new Subscription();

  public readonly mediaList = ['google', 'ical', 'outlook'];
  public readonly toggleNameEnum = ToggleNameEnum;
  public readonly vtmnPlayButtonSize = VtmnPlayButtonSizeEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;

  public decatClubUrl: string;
  public items: CheckoutItem[];
  public confirmType: string;
  public qrCode: string;
  public pdfUrl: string;
  public walletUrl: string;
  public svg: string;
  public sanitizedSvg: SafeResourceUrl;
  public hasError: string;
  public isLoading = true;
  public activityEligible = false;
  public title: string;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly activityService: ActivityService,
    private readonly checkoutService: CheckoutService,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly titleService: TitleService,
    private readonly talkService: TalkService,
    private readonly toggleService: ToggleService,
    private readonly userService: UserService,
    private readonly translateService: TranslateService,
    private readonly bookingService: BookingService,
    private readonly sanitizer: DomSanitizer
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.checkout.confirmation.title');

    this.subscriptions.add(
      this.checkoutService.getItems().subscribe((checkoutItems) => {
        this.items = checkoutItems;
      })
    );

    this.subscriptions.add(
      this.activatedRoute?.queryParamMap?.subscribe((params) => {
        if (params.get('bookingItemId')) {
          this.bookingService
            .getBookingItems(params.get('bookingItemId'))
            .subscribe({
              next: (item) => {
                this.activityService
                  .getActivity(item.sku.split('-')[0])
                  .subscribe({
                    next: (activity) => {
                      if (activity.type === 'EVENT' && !this.items) {
                        this.activityEligible = true;
                      }
                    },
                  });
              },
              error: (err) => {
                console.log('Error : ', err.error.message);
                this.hasError = 'We cannot get Items';
              },
            });

          this.bookingService.getQrCode(params.get('bookingItemId')).subscribe({
            next: (item: QrCodeBookingInterface) => {
              this.pdfUrl = item.pdfUrl;
              this.walletUrl = item.walletUrl ? item.walletUrl : '';
              this.title = item.title;
              this.sanitizedSvg = this.sanitizer.bypassSecurityTrustHtml(
                item.svg
              );
            },
            complete: () => {
              this.isLoading = false;
            },
            error: (err) => {
              console.log('Error : ', err.error.message);
              this.hasError = 'We cannot display your QRCode';
              this.isLoading = false;
            },
          });
        }
      })
    );

    this.subscriptions.add(
      this.activatedRoute.data.subscribe((data) => {
        this.confirmType = data.type;
        this.isLoading = false;
      })
    );

    this.checkoutService.emptyCart();

    // reset cart
    this.checkoutService.resetCheckout();

    // we mount an hidden inbox ... so the talkjs user is updated
    // with email address
    this.subscriptions.add(
      this.userService
        .isAuthenticated()
        .pipe(
          mergeMap((isLogged) => {
            if (!isLogged) {
              return of(undefined);
            }

            return this.talkService.createCurrentSession().pipe(
              mergeMap((session) => {
                const loggedUser = this.userService.getUser();

                return this.talkService.createInbox(session, {
                  identifier: loggedUser?.identifier,
                  name: loggedUser?.fullname || loggedUser?.identifier,
                  email: loggedUser?.email,
                });
              }),
              mergeMap((inbox) => from(inbox.mount(this.inbox.nativeElement)))
            );
          })
        )
        .subscribe()
    );

    // update the user with the contact phone number data asked at the subscription step
    this.subscriptions.add(
      this.userService
        .updateUser({
          ...this.userService.getUser(),
          ...this.checkoutService.contactPhoneNumberData,
        })
        .subscribe()
    );

    this.decatClubUrl = this.toggleService.getVariantString(
      this.toggleNameEnum.DecatClubUrl,
      null
    );
  }

  public get redirectPath(): string {
    if (this.confirmType === 'paying') {
      return '/attendee.bookings/attendee.waiting';
    }
    return '/attendee.homepage';
  }

  public get isActivityEvent(): boolean {
    return this.items && this.items[0]?.activity?.type === 'EVENT';
  }

  public getMediaImageSrc(media: string): string {
    return `assets/agenda/${media}.svg`;
  }

  public getMediaTitle(media: string): string {
    return this.translateService.instant('checkout.confirmation.' + media);
  }

  public redirect(media: string, item: CheckoutItem): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    window.location.href = this.mediaMethods[media](
      item.activity.name,
      item.activity.description,
      item.activity.fullAddress,
      new Date(item.timeslot.startDate),
      new Date(item.timeslot.endDate)
    );
  }
}
